import React, { useEffect, useRef } from "react";

/*
 * Standalone custom cursor (glowing dot + lagging ring) for jjfv2 pages that
 * don't use JjfShell — e.g. Blogs, single blog articles and Newsletters.
 * It mirrors the cursor logic built into JjfShell: it finds the nearest
 * `.jjfv2` wrapper, drives a dot that tracks the pointer and a ring that
 * follows with easing, and toggles hover/text/press states. Native cursor is
 * hidden only on fine-pointer, non-reduced-motion devices (never on touch).
 * All visual styling lives in homeV2.css (loaded via HeaderV2).
 */
export default function JjfCursor() {
  const ringRef = useRef(null);

  useEffect(() => {
    const ring = ringRef.current;
    if (!ring) return;
    const root = ring.closest(".jjfv2");
    if (!root) return;
    const dot = root.querySelector(".cur-dot");
    if (!dot) return;

    const fine = window.matchMedia("(hover:hover) and (pointer:fine)").matches;
    const reduce = window.matchMedia("(prefers-reduced-motion:reduce)").matches;
    if (!fine || reduce) return;

    root.classList.add("cursor-on");
    document.body.classList.add("jjfv2-no-native-cursor");

    let mx = window.innerWidth / 2;
    let my = window.innerHeight / 2;
    let rx = mx;
    let ry = my;
    let ready = false;
    let raf;

    const onMove = (e) => {
      mx = e.clientX;
      my = e.clientY;
      dot.style.transform = `translate(${mx}px,${my}px) translate(-50%,-50%)`;
      if (!ready) {
        ready = true;
        root.classList.add("cursor-ready");
        rx = mx;
        ry = my;
      }
    };
    const loop = () => {
      rx += (mx - rx) * 0.18;
      ry += (my - ry) * 0.18;
      ring.style.transform = `translate(${rx}px,${ry}px) translate(-50%,-50%)`;
      raf = requestAnimationFrame(loop);
    };
    loop();

    const hoverSel =
      "a,button,.nav-top,.blog-card,.sidebar-menu-item,.mobile-menu-item,.gallery-item,.pdf-badge,.pagination-btn,.see-more-link";
    const onOver = (e) => {
      if (e.target.closest("input,textarea,select")) {
        root.classList.add("cur-text");
        root.classList.remove("cur-hover");
      } else if (e.target.closest(hoverSel)) {
        root.classList.add("cur-hover");
        root.classList.remove("cur-text");
      } else {
        root.classList.remove("cur-hover", "cur-text");
      }
    };
    const onDown = () => root.classList.add("cur-down");
    const onUp = () => root.classList.remove("cur-down");
    const onLeave = () => root.classList.remove("cursor-ready");
    const onEnter = () => root.classList.add("cursor-ready");

    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onOver);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      document.body.classList.remove("jjfv2-no-native-cursor");
      root.classList.remove("cursor-on", "cursor-ready", "cur-hover", "cur-text", "cur-down");
    };
  }, []);

  return (
    <>
      <div className="cur-ring" ref={ringRef} aria-hidden="true"></div>
      <div className="cur-dot" aria-hidden="true"></div>
    </>
  );
}
